// AI service for interior design suggestions
import { collection, addDoc, serverTimestamp, doc, onSnapshot } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage, auth } from '../firebase.js';
import { CameraService } from './camera.js';
import { buildEditPrompt, buildEditPromptWithOverlay } from './edit-prompt.js';
import { buildAnalysisPrompt } from '../shared/design-prompt.js';
import { INTERIOR_STYLES } from '../shared/style-catalog.js';
import { getSeasonOverrides } from './season-config.js';
export {
  BUDGET_TIERS, DESIGN_MODES, SPACE_CATEGORIES, DETECTED_INPUT_TYPES,
  EXTERIOR_STYLES, GARDEN_STYLES, SEASON_OPTIONS, TIME_OF_DAY_OPTIONS,
} from '../shared/style-catalog.js';
// `export … from` 은 이 파일 안에 이름을 만들지 않는다 — 여기서 쓰는 건 따로 import.
// (2.3.0 에서 DETECTED_INPUT_TYPES 를 빠뜨려 게스트 saveDesign 이 전부 ReferenceError 로 죽었다.)
import { EXTERIOR_STYLES, GARDEN_STYLES, DETECTED_INPUT_TYPES } from '../shared/style-catalog.js';

const CUSTOM_HISTORY_KEY = 'voda_custom_style_history';
const MAX_HISTORY = 10;

// 업로드 파일은 이름이 매번 새로 생기는 불변 객체라 1년 캐시가 안전하다.
// 안 주면 Firebase 기본값 `private, max-age=0` 이 붙어 그리드를 열 때마다
// 장마다 재검증이 나간다. 서버 onDesignThumb 도 같은 값을 덧씌우지만
// (구버전 앱 대비), 처음부터 붙여 두면 트리거가 돌기 전에도 캐시된다.
const IMMUTABLE_UPLOAD = { cacheControl: 'public, max-age=31536000, immutable' };

export const AIService = {
  // Interior styles — 원본은 src/shared/style-catalog.js (서버와 공유)
  interiorStyles: INTERIOR_STYLES,

  // Custom style history (localStorage)
  getCustomStyleHistory() {
    try {
      return JSON.parse(localStorage.getItem(CUSTOM_HISTORY_KEY) || '[]');
    } catch {
      return [];
    }
  },

  saveCustomStyleToHistory(text) {
    if (!text || !text.trim()) return;
    try {
      const history = this.getCustomStyleHistory();
      const filtered = history.filter(h => h !== text.trim());
      const updated = [text.trim(), ...filtered].slice(0, MAX_HISTORY);
      localStorage.setItem(CUSTOM_HISTORY_KEY, JSON.stringify(updated));
    } catch (e) {
      console.warn('Failed to save custom style history:', e);
    }
  },

  // season 창 안인지. from/to 는 'MM-DD'. 12-20 → 01-10 처럼 해를 넘기는
  // 창도 받는다 (from > to 면 넘어가는 것으로 본다).
  //
  // 창은 Firestore `config/seasons` 로 덮어쓸 수 있다 (services/season-config.js).
  // 여기 박힌 값은 그게 없을 때의 기본값일 뿐이다 — 설날처럼 해마다 날짜가
  // 움직이는 시즌은 콘솔에서 바꾼다. 재배포·스토어 심사 없이.
  isInSeason(style, now = new Date(), key = null) {
    const base = style?.season;
    if (!base) return true;                  // 시즌 없는 스타일은 항상 노출
    const cfg = getSeasonOverrides();
    if (cfg?.showAll) return true;           // 미리보기 스위치
    const ov = key ? cfg?.windows?.[key] : null;
    if (ov?.enabled === false) return false; // 콘솔에서 끈 시즌
    const from = ov?.from || base.from;
    const to = ov?.to || base.to;
    const mmdd = `${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    return from <= to
      ? mmdd >= from && mmdd <= to
      : mmdd >= from || mmdd <= to;
  },

  // Resolve the right style set for a given space category.
  // 시즌이 지난 스타일은 목록에서 뺀다 — 단, 이미 그 스타일로 만든 디자인의
  // 이름을 찾을 땐 styleNameFor 가 원본 카탈로그를 직접 보므로 영향 없다.
  styleCatalog(spaceCategory) {
    const base = spaceCategory === 'exterior' ? EXTERIOR_STYLES
      : spaceCategory === 'garden' ? GARDEN_STYLES
      : this.interiorStyles;
    // 세 카테고리 모두 같은 시즌 창을 쓴다 — 키가 같으므로 config/seasons 의
    // 항목 하나가 interior/exterior/garden 을 한꺼번에 켜고 끈다.
    return Object.fromEntries(
      Object.entries(base).filter(([k, v]) => this.isInSeason(v, new Date(), k))
    );
  },

  // Look up a style's display name across all catalogs. For legacy designs
  // the spaceCategory may be missing; try interior first then fall back.
  styleNameFor(style, spaceCategory) {
    if (!style) return '';
    const cat = this.styleCatalog(spaceCategory);
    if (cat[style]?.name) return cat[style].name;
    // Fallback search across all catalogs in case spaceCategory is wrong.
    return (
      this.interiorStyles[style]?.name
      || EXTERIOR_STYLES[style]?.name
      || GARDEN_STYLES[style]?.name
      || style
    );
  },

  // Generate prompt for the text analysis pass — 원본은 src/shared/design-prompt.js
  // (서버 생성 작업과 공유). 구버전 경로(generateDesign)가 아직 이걸 보낸다.
  generatePrompt(...args) {
    return buildAnalysisPrompt(...args);
  },

  buildEditPrompt,

  // Send the source image + a "red overlay" composite of the brush region
  // + edit prompt to editDesignRegion. Returns { generatedImage, mimeType }.
  async editDesignRegion({ sourceDesignId, photoIndex, replacement, overlayBlob }) {
    if (!overlayBlob) throw new Error('OVERLAY_REQUIRED');
    const prompt = buildEditPromptWithOverlay(replacement);

    // Convert overlay blob → base64 (without the data: prefix).
    const overlayBase64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(overlayBlob);
    });

    const functionUrl = 'https://us-central1-voda-7647c.cloudfunctions.net/editDesignRegion';
    const headers = { 'Content-Type': 'application/json' };
    const user = auth.currentUser;
    if (user) {
      try {
        const token = await user.getIdToken();
        headers.Authorization = `Bearer ${token}`;
      } catch (e) {
        console.warn('Failed to attach ID token:', e);
      }
    }

    const response = await fetch(functionUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        data: { sourceDesignId, photoIndex, replacement, prompt, overlayBase64, overlayMimeType: 'image/jpeg' },
      }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const err = new Error(errorData.error || 'Edit request failed');
      err.code = errorData.error;
      err.status = response.status;
      throw err;
    }
    return response.json();
  },

  // Generate design: accepts array of blobs.
  // When `referenceImageBlob` is provided AND style === 'reference', the
  // reference photo is appended to the user's room images and the prompt
  // tells the AI to match that visual reference (tone / materials / mood).
  // ── 서버 생성 작업 (2.3.0, functions/design-jobs.js) ──────────────────
  // 로그인 사용자는 이 경로. 사진을 먼저 Storage 에 올리고 작업만 맡긴다 —
  // 앱이 백그라운드로 가도 서버가 끝까지 만들고 푸시로 알린다. 게스트는 아래
  // generateDesign (동기) 그대로.
  async startDesignJob(params) {
    const user = auth.currentUser;
    if (!user || user.isAnonymous) throw new Error('AUTH_REQUIRED');
    const token = await user.getIdToken();
    const response = await fetch('https://us-central1-voda-7647c.cloudfunctions.net/startDesignJob', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ data: { galleryAware: true, ...params } }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      const err = new Error(body.error || 'Request failed');
      err.code = body.error;
      err.status = response.status;
      if (body.requiredCredits !== undefined) err.required = body.requiredCredits;
      if (body.availableCredits !== undefined) err.available = body.availableCredits;
      throw err;
    }
    return body; // { designId, cost }
  },

  // 작업이 끝날 때까지 designs/{id} 를 지켜본다. status 가 generating 이 아니게
  // 되면 문서 데이터로 resolve. 앱이 백그라운드에 있던 사이 끝났어도 돌아오면
  // 스냅샷이 바로 최종 상태를 준다.
  waitForDesign(designId, { timeoutMs = 9 * 60 * 1000 } = {}) {
    return new Promise((resolve, reject) => {
      let unsub = () => {};
      const timer = setTimeout(() => { unsub(); reject(new Error('JOB_TIMEOUT')); }, timeoutMs);
      unsub = onSnapshot(doc(db, 'designs', designId), (snap) => {
        const d = snap.data();
        if (!d || d.status === 'generating') return;
        clearTimeout(timer);
        unsub();
        resolve({ id: snap.id, ...d });
      }, (e) => { clearTimeout(timer); reject(e); });
    });
  },

  async generateDesign(imageBlobs, style, customStyleDescription = '', additionalRequest = '', promptLang = 'English', budget = 'mid', mode = 'redesign', spaceCategory = 'interior', exteriorOptions = null, referenceImageBlob = null) {
    if (!Array.isArray(imageBlobs)) imageBlobs = [imageBlobs];
    const userImageCount = imageBlobs.length;
    const allImages = (style === 'reference' && referenceImageBlob)
      ? [...imageBlobs, referenceImageBlob]
      : imageBlobs;

    // Convert all blobs to base64
    const base64Images = await Promise.all(
      allImages.map(blob => CameraService.blobToBase64(blob))
    );

    const functionUrl = 'https://us-central1-voda-7647c.cloudfunctions.net/generateDesign';

    const headers = { 'Content-Type': 'application/json' };
    // Send verified ID token so the backend can gate credits.
    const user = auth.currentUser;
    if (user) {
      try {
        const token = await user.getIdToken();
        headers.Authorization = `Bearer ${token}`;
      } catch (e) {
        console.warn('Failed to attach ID token:', e);
      }
    }

    const response = await fetch(functionUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        data: {
          images: base64Images,
          // 이 번들은 커스텀 스타일 공개 정책(공지 배너 + opt-out)을 노출한다.
          // 이 플래그가 없는 구버전 앱의 커스텀은 서버가 비공개로 기록한다
          // (functions/style-stats.js) — 모르고 쓴 사람 것이 공개되지 않게.
          galleryAware: true,
          // Reference Style 사용 시 base64Images 의 마지막은 reference 사진.
          // Backend 는 analysis 에 모든 이미지 / generation 에는 처음 N개 (user 방 사진) 만 사용.
          userImageCount,
          style,
          customStyleDescription,
          additionalRequest,
          budget,
          mode,
          spaceCategory,
          exteriorOptions: exteriorOptions || null,
          prompt: this.generatePrompt(style, customStyleDescription, additionalRequest, userImageCount, promptLang, budget, mode, spaceCategory, exteriorOptions)
        }
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const err = new Error(errorData.error || 'Request failed');
      err.code = errorData.error;
      err.status = response.status;
      // 402 INSUFFICIENT_CREDITS 일 때 서버가 실어 보내는 숫자 —
      // "3개 필요, 2개 보유" 를 그대로 말해 주기 위함 (functions/index.js).
      // 2.3.0 단위(크레딧)가 있으면 그걸 — required/available 은 구버전용 디자인 개수.
      if (errorData.requiredCredits !== undefined) err.required = errorData.requiredCredits;
      else if (errorData.required !== undefined) err.required = errorData.required;
      if (errorData.availableCredits !== undefined) err.available = errorData.availableCredits;
      else if (errorData.available !== undefined) err.available = errorData.available;
      throw err;
    }

    const designData = await response.json();
    return designData;
  },

  // Save design to Firestore. `sourceDesignId` links a design back to the
  // one it was edited from (Phase 9-3 부분 편집). `spaceCategory` +
  // `exteriorOptions` route the design to the right style set + outdoor
  // metadata (Phase 9-5).
  async saveDesign(designData, originalImageUrls, style, customStyleInput = null, additionalRequest = null, lang = null, budget = 'mid', mode = 'redesign', sourceDesignId = null, spaceCategory = 'interior', exteriorOptions = null, referenceImageUrl = null, outputType = null) {
    try {
      // Phase 9-6 auto-detect: trust the AI's classification, but normalize.
      const detectedInputType = DETECTED_INPUT_TYPES.includes(designData.detectedInputType)
        ? designData.detectedInputType
        : 'photo';
      const floorplanRoomFocus = (detectedInputType === 'floorplan' && typeof designData.floorplanRoomFocus === 'string' && designData.floorplanRoomFocus.trim())
        ? designData.floorplanRoomFocus.trim().slice(0, 80)
        : null;

      const cleanedData = {
        spaceAnalysis: designData.spaceAnalysis,
        styleDescription: designData.styleDescription,
        colorPalette: designData.colorPalette,
        furnitureRecommendations: designData.furnitureRecommendations,
        decorativeElements: designData.decorativeElements,
        lightingPlan: designData.lightingPlan,
        estimatedCost: designData.estimatedCost,
        tips: designData.tips,
        originalImageUrls: Array.isArray(originalImageUrls) ? originalImageUrls : [originalImageUrls].filter(Boolean),
        generatedImageUrls: designData.generatedImageUrls || [],
        style,
        customStyleInput,
        additionalRequest,
        budget: budget || 'mid',
        mode: mode || 'redesign',
        spaceCategory: spaceCategory || 'interior',
        detectedInputType,
        ...(floorplanRoomFocus && { floorplanRoomFocus }),
        ...(exteriorOptions && { exteriorOptions }),
        ...(referenceImageUrl && { referenceImageUrl }),
        // 평면도 모드 (2.4.0) — 결과 화면이 슬라이더/한 장 보기를 가른다.
        ...(outputType && { outputType }),
        ...(sourceDesignId && { sourceDesignId }),
        timestamp: serverTimestamp(),
        userId: auth.currentUser?.uid || 'anonymous',
        hasGeneratedImage: (designData.generatedImageUrls || []).some(Boolean),
        imageGenerationError: designData.imageGenerationError || null,
        photoCount: Array.isArray(originalImageUrls) ? originalImageUrls.length : 1,
        status: 'success',
        ...(lang && { lang }),
      };
      const docRef = await addDoc(collection(db, 'designs'), cleanedData);
      return docRef.id;
    } catch (error) {
      console.error('Design save failed:', error);
      throw error;
    }
  },

  // Save failed design attempt
  async saveFailedDesign({ originalImageUrls, style, customStyleInput }) {
    try {
      const docRef = await addDoc(collection(db, 'designs'), {
        status: 'failed',
        originalImageUrls: Array.isArray(originalImageUrls) ? originalImageUrls : [originalImageUrls].filter(Boolean),
        style,
        customStyleInput,
        timestamp: serverTimestamp(),
        userId: auth.currentUser?.uid || 'anonymous',
      });
      return docRef.id;
    } catch (e) {
      console.error('Failed to save failed design:', e);
      return null;
    }
  },

  // Log error to Firestore
  async logError(error, context = {}) {
    try {
      await addDoc(collection(db, 'errorLogs'), {
        message: error.message || String(error),
        name: error.name || null,
        stack: error.stack || null,
        timestamp: serverTimestamp(),
        userId: auth.currentUser?.uid || 'anonymous',
        userAgent: navigator.userAgent,
        ...context,
      });
    } catch (e) {
      console.error('Failed to save error log:', e);
    }
  },

  // Upload original image to Storage
  async uploadOriginalImage(imageBlob) {
    const timestamp = Date.now();
    const fileName = `originals/${timestamp}_${Math.random().toString(36).substr(2, 9)}.jpg`;
    const storageRef = ref(storage, fileName);
    const snapshot = await uploadBytes(storageRef, imageBlob, IMMUTABLE_UPLOAD);
    return getDownloadURL(snapshot.ref);
  },

  // 부분 편집의 "빨갛게 칠한 사본" — 서버 작업(startDesignJob type:'edit')이
  // 버킷에서 받아 간다 (2.3.0). originals/ 규칙(이미지, 10MB)을 그대로 쓴다.
  async uploadEditOverlay(overlayBlob) {
    const fileName = `originals/edit-overlays/${Date.now()}_${Math.random().toString(36).substr(2, 9)}.jpg`;
    const snapshot = await uploadBytes(ref(storage, fileName), overlayBlob, IMMUTABLE_UPLOAD);
    return getDownloadURL(snapshot.ref);
  },

  // Upload reference photo (Reference Style 모드) — 디자인 doc 의 referenceImageUrl
  // 에 저장돼서 use-this-style 로 다시 시작할 때 같은 reference 가 자동으로 채워짐.
  async uploadReferenceImage(imageBlob) {
    const timestamp = Date.now();
    const fileName = `references/${timestamp}_${Math.random().toString(36).substr(2, 9)}.jpg`;
    const storageRef = ref(storage, fileName);
    const snapshot = await uploadBytes(storageRef, imageBlob, IMMUTABLE_UPLOAD);
    return getDownloadURL(snapshot.ref);
  },

  // Upload generated design image to Storage
  async uploadDesignImage(imageBase64, mimeType = 'image/jpeg') {
    const byteCharacters = atob(imageBase64);
    const byteArray = new Uint8Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteArray[i] = byteCharacters.charCodeAt(i);
    }
    const blob = new Blob([byteArray], { type: mimeType });
    const timestamp = Date.now();
    const fileName = `generated_designs/${timestamp}_${Math.random().toString(36).substr(2, 9)}.jpg`;
    const storageRef = ref(storage, fileName);
    const snapshot = await uploadBytes(storageRef, blob, IMMUTABLE_UPLOAD);
    return getDownloadURL(snapshot.ref);
  },
};
